import React, { useState } from 'react'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { PieImage } from 'components/common'
import type { EventsManageAttendeeBookingsQueryEventType } from 'pages/RepClubs/Events/event.type'
import type { AttendeeBookingsQueryAttendeeTypeExtended } from 'utils/attendee-helpers'
import type { EventAttendeesBookingInfo } from 'graphql/generated/scheme'

interface EventFullModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	event?: Partial<EventsManageAttendeeBookingsQueryEventType> | null
	attendee: AttendeeBookingsQueryAttendeeTypeExtended | null
	booking?: Partial<EventAttendeesBookingInfo> | null
	onApprove: () => void
}

export const EventFullModal: React.FC<EventFullModalProps> = ({ open, setOpen, event, onApprove }) => {
	return (
		<ContentModal
			open={open}
			setOpen={setOpen}
			isModal
			className="sm:py-[56px] sm:px-[72px]"
			hideHeader={true}
			customSize="sm:!max-w-[474px]"
		>
			<div className="relative flex flex-col items-center">
				<div>
					<div>
						<h3 className="text-2xl font-bold flex justify-center items-center gap-2">Event Full</h3>
					</div>
					<div className="mt-5">Your event has reached the max number of attendees</div>
					<div className="flex justify-center items-center py-9 relative">
						<PieImage
							className="w-[200px] h-auto rounded-xl overflow-hidden flex-shrink-0"
							images={[event?.avatarUrl]}
						/>
					</div>
					<div>Would you like to approve this person anyway?</div>
				</div>
				<div className="flex justify-between w-full gap-4 mt-[43px]">
					<button
						type="button"
						className="w-[157px] rounded-3xl text-center transition-all duration-300 bg-primary text-white font-bold block h-10 shadow-button backdrop-blur-lg hover:bg-primary-hover disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
						onClick={onApprove}
					>
						Approve
					</button>
					<button
						type="button"
						className="w-[157px] rounded-3xl text-center transition-all duration-300 bg-secondary-button text-white font-bold block h-10 shadow-button backdrop-blur-lg hover:bg-secondary-button-hover disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
						onClick={() => setOpen(false)}
					>
						Go Back
					</button>
				</div>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface EventFullModalManagedProps {
	event?: Partial<EventsManageAttendeeBookingsQueryEventType> | null
	attendee: AttendeeBookingsQueryAttendeeTypeExtended | null
	booking?: Partial<EventAttendeesBookingInfo> | null
	onApprove: () => void
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'event-full': EventFullModalManagedProps
	}
}

export function ManagedEventFullModal({
	onClose,
	onApprove,
	...rest
}: EventFullModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<EventFullModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onApprove={() => { onApprove(); handleClose() }}
			{...rest}
		/>
	)
}
